'use client';

import Link from 'next/link';
import {
  ArrowRight,
  Clock,
  Headphones,
  Plane,
  ShieldOff,
  Ticket,
  Timer,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useGeoPricing } from '@/context/GeoPricingContext';
import useBookingStore from '@/store/useBookingStore';
import { calculateOrderPrice } from '@/config/pricingCatalog';
import ScrollAnimation from './ScrollAnimation';

const STANDARD_FEATURES = [
  { Icon: Ticket, label: 'Standard Flight Itinerary' },
  { Icon: Plane, label: 'Real Flight Data' },
  { Icon: ShieldOff, label: 'Flight Reservation Document' },
  { Icon: Timer, label: '10–60 mins Delivery' },
];

export default function PricingPackages() {
  const { formatPrice, currencyMeta: geoMeta } = useGeoPricing();
  const storeCurrency = useBookingStore((s) => s.currency);

  // Use user-selected currency if available, otherwise fall back to geo-detected
  const activeCurrency = storeCurrency || geoMeta?.code || 'USD';

  const oneWayPricing = calculateOrderPrice(activeCurrency, 'onewaytrip');
  const roundTripPricing = calculateOrderPrice(activeCurrency, 'roundtrip');

  const priceSingle = `${oneWayPricing.symbol}${formatPrice(oneWayPricing.amount)}`;
  const priceRound = `${roundTripPricing.symbol}${formatPrice(roundTripPricing.amount)}`;

  return (
    <section
      id="pricing"
      className="relative w-full overflow-hidden py-10 md:py-14"
      aria-labelledby="pricing-heading"
    >
      <div className="custom-mx relative z-10 mx-auto max-w-7xl">
        <div className="mx-auto mb-10 max-w-3xl text-center md:mb-12">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-primary">
            Main plan
          </p>
          <h2
            id="pricing-heading"
            className="text-2xl font-extrabold tracking-tight text-slate-850 md:text-3xl lg:text-4xl"
          >
            Standard Reservation
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
            Suitable for visa applications and travel documentation where flight itinerary proof is requested.
            Used by travelers for visa and travel-documentation needs.
          </p>
        </div>

        <ul className="mx-auto mb-12 grid max-w-3xl gap-3 sm:grid-cols-2">
          {STANDARD_FEATURES.map(({ Icon, label }) => (
            <li
              key={label}
              className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3 text-left text-sm font-medium text-slate-800 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-primary shadow-sm ring-1 ring-slate-100">
                <Icon className="h-5 w-5" strokeWidth={2.25} aria-hidden />
              </span>
              <span>{label}</span>
            </li>
          ))}
        </ul>

        <p className="mx-auto mb-8 max-w-2xl text-center text-sm leading-relaxed text-slate-500">
          Digital document service. No airline ticket purchase is included. Final eligibility and acceptance depend on the relevant authority&apos;s requirements.
        </p>

        <div className="mx-auto grid max-w-4xl gap-5 md:grid-cols-2">
          <ScrollAnimation delay={0.1} className="h-full">
            <article
              className="flex h-full flex-col rounded-2xl border border-slate-150 bg-slate-50/50 p-6 md:p-8 transition-all duration-300 hover:border-primary/20 hover:bg-white hover:shadow-sm"
            >
              <p className="text-xs font-semibold tracking-wide text-slate-400">
                One Way · Standard Reservation
              </p>
              <p className="mt-3 text-4xl font-semibold tabular-nums text-slate-900">{priceSingle}</p>
              <p className="mt-2 flex items-center gap-2 text-sm font-medium text-primary">
                <Clock className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />
                Delivered within 10–60 minutes
              </p>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">
                Single-direction reservation for visa files, onward-travel proof, or when you only need departure details.
              </p>
            </article>
          </ScrollAnimation>

          <ScrollAnimation delay={0.25} className="h-full">
            <article
              className="flex h-full flex-col rounded-2xl border border-primary/20 bg-gradient-to-b from-white to-tertiary/20 p-6 md:p-8 shadow-sm transition-all duration-300 hover:border-primary/30"
            >
              <p className="text-xs font-semibold tracking-wide text-primary">
                Round Trip · Standard Reservation
              </p>
              <p className="mt-3 text-4xl font-semibold tabular-nums text-slate-900">{priceRound}</p>
              <p className="mt-2 flex items-center gap-2 text-sm font-medium text-primary">
                <Clock className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />
                Delivered within 10–60 minutes
              </p>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">
                Outbound and return on one reservation document — commonly requested for Schengen, UK, and Canada applications.
              </p>
            </article>
          </ScrollAnimation>
        </div>

        {/* ── Product Transparency: What you receive vs What you do NOT receive ── */}
        <div className="mx-auto mt-10 max-w-4xl rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm">
          <h3 className="text-base font-bold text-slate-850 text-center mb-6">
            Product Transparency &amp; Service Expectations
          </h3>
          <div className="grid gap-6 md:grid-cols-2">
            {/* What you receive */}
            <div className="rounded-xl bg-emerald-50/50 border border-emerald-100 p-5 space-y-3">
              <p className="text-xs font-bold tracking-wider text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" /> What You Receive
              </p>
              <ul className="space-y-2 text-xs text-slate-700 font-medium">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span>Digital flight itinerary / reservation document</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span>Passenger and flight route details</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span>Travel dates and itinerary information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span>PDF delivery by email</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span>Review of submitted details before processing</span>
                </li>
              </ul>
            </div>

            {/* What you do NOT receive */}
            <div className="rounded-xl bg-slate-50 border border-slate-200/80 p-5 space-y-3">
              <p className="text-xs font-bold tracking-wider text-slate-700 flex items-center gap-2">
                <XCircle className="h-4 w-4 text-slate-400" /> What You Do NOT Receive
              </p>
              <ul className="space-y-2 text-xs text-slate-600 font-medium">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 font-bold">✕</span>
                  <span>Airline boarding pass</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 font-bold">✕</span>
                  <span>Paid / guaranteed airline ticket</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 font-bold">✕</span>
                  <span>Visa approval guarantee</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 font-bold">✕</span>
                  <span>Guaranteed embassy acceptance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 font-bold">✕</span>
                  <span>Guaranteed immigration approval</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-4xl rounded-2xl bg-slate-50/70 px-6 py-6 md:flex md:items-center md:justify-between md:px-8 gap-4">
          <div className="flex items-start gap-3 md:items-center">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-tertiary text-primary">
              <Headphones className="h-5 w-5" strokeWidth={2.25} aria-hidden />
            </span>
            <div>
              <p className="font-semibold text-slate-900">Priority support — included</p>
              <p className="mt-1 text-sm text-slate-600">
                Stuck on a route, a name format, or a tight deadline? You are not routed to a bot-only queue — humans
                help when it matters.
              </p>
            </div>
          </div>
          <Link
            href="/book"
            prefetch={false}
            className="mt-5 inline-flex min-h-[48px] items-center justify-center gap-2 self-start rounded-full bg-gradient-to-r from-primary to-secondary px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary/25 transition hover:-translate-y-0.5 md:mt-0 shrink-0"
          >
            Start booking
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}
